import type { ComponentProps, ReactNode } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DOMPurify from "dompurify";
import { FoodsTags } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";
import { getConsentCategoryConfig, hasKnownConsentCategory } from "./consentCategoryConfig";
import { formatConsentDateTime } from "./consentDateFormat";
import { getConsentDisplayTitle } from "./consentDisplay";

type FoodsTagVariant = ComponentProps<typeof FoodsTags>["variant"];

export type ConsentDocumentPreview = {
    acceptedAt?: string | null;
    category?: string | null;
    content?: string | null;
    description?: string | null;
    documentUrl?: string | null;
    itemLabel?: string | null;
    statusTag?: {
        label: string;
        variant: FoodsTagVariant;
    };
    summary?: string | null;
    title: string;
    version?: string | null;
};

type ConsentDocumentPreviewDialogProps = ConsentDocumentPreview & {
    actions?: ReactNode;
    onClose: () => void;
    open: boolean;
    showDescription?: boolean;
};

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const stripHtml = (value: string) => {
    const sanitizedValue = DOMPurify.sanitize(value);

    if (typeof document === "undefined") {
        return normalizeWhitespace(sanitizedValue.replace(/<[^>]*>/g, " "));
    }

    const container = document.createElement("div");
    container.innerHTML = sanitizedValue;

    return normalizeWhitespace(container.textContent ?? "");
};

const hasVisibleRichText = (value: string | null | undefined) => {
    if (!value?.trim()) {
        return false;
    }

    return stripHtml(value).length > 0;
};

export const getConsentPreviewText = (...values: Array<string | null | undefined>) => {
    const previewSource = values.find(hasVisibleRichText);

    return previewSource ? stripHtml(previewSource) : "";
};

export const ConsentDocumentPreviewDialog = ({
    acceptedAt,
    actions,
    category,
    content,
    description,
    documentUrl,
    itemLabel,
    onClose,
    open,
    showDescription = false,
    statusTag,
    summary,
    title,
    version,
}: ConsentDocumentPreviewDialogProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const locale = i18n?.resolvedLanguage || i18n?.language;
    const visibleDescription = showDescription && hasVisibleRichText(description) ? description : null;
    const visibleSummary = hasVisibleRichText(summary) ? summary : null;
    const visibleContent = hasVisibleRichText(content) ? content : null;
    const visibleDocumentUrl = documentUrl?.trim() ? documentUrl : null;
    const descriptionLabel = t("settings.consent.item.description", "Internal description");
    const hasBody = Boolean(visibleDescription || visibleSummary || visibleContent || visibleDocumentUrl);
    const categoryConfig = getConsentCategoryConfig(category);
    const categoryLabel = t(categoryConfig.labelKey, categoryConfig.labelFallback);
    const showCategory = hasKnownConsentCategory(category);
    const displayTitle = getConsentDisplayTitle(title, version);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: theme.spacing(2),
                },
            }}
        >
            <DialogTitle sx={{ px: theme.spacing(4), pt: theme.spacing(3), pb: theme.spacing(2) }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {displayTitle}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                            {version && <FoodsTags label={`v${version}`} variant="tags-grey" />}
                            {showCategory && <FoodsTags label={categoryLabel} variant="tags-grey" />}
                            {statusTag && <FoodsTags label={statusTag.label} variant={statusTag.variant} />}
                            {acceptedAt && (
                                <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                    {t("consent.drawer.accepted_on", "Accepted on {{date}}", {
                                        date: formatConsentDateTime(acceptedAt, locale),
                                    })}
                                </Typography>
                            )}
                            {itemLabel && (
                                <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                    {itemLabel}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                    <IconButton
                        aria-label={t("common.close", "Close")}
                        onClick={onClose}
                        size="small"
                        sx={{
                            border: `1px solid ${theme.custom.grey[500]}`,
                            borderRadius: theme.spacing(1),
                            minHeight: 44,
                            minWidth: 44,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ maxHeight: "60vh", px: theme.spacing(4), py: theme.spacing(3) }}>
                {hasBody ?
                    <Stack gap={theme.spacing(2)}>
                        {visibleDescription && (
                            <Box
                                component="blockquote"
                                aria-label={descriptionLabel}
                                sx={{
                                    borderLeft: `4px solid ${theme.custom.grey[1500]}`,
                                    color: theme.custom.grey[2000],
                                    m: 0,
                                    pl: theme.spacing(2),
                                    py: theme.spacing(0.5),
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.custom.grey[2000],
                                        fontWeight: 700,
                                        mb: theme.spacing(0.5),
                                    }}
                                >
                                    {descriptionLabel}
                                </Typography>
                                <HTMLBox value={visibleDescription} />
                            </Box>
                        )}
                        {visibleSummary && <HTMLBox value={visibleSummary} />}
                        {visibleContent && <HTMLBox value={visibleContent} />}
                        {visibleDocumentUrl && (
                            <Stack alignItems="flex-start">
                                <Button
                                    component="a"
                                    href={visibleDocumentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="primary"
                                    startIcon={<OpenInNewIcon fontSize="small" />}
                                    sx={{ minHeight: 44, textTransform: "none" }}
                                >
                                    {t("settings.consent.preview.open_document", "Ouvrir le document")}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                :   <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                        {t("settings.consent.preview.empty", "No content is available for this preview.")}
                    </Typography>
                }
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: theme.spacing(4), py: theme.spacing(2), gap: theme.spacing(1.5) }}>
                {actions ?? (
                    <Button variant="primary" onClick={onClose} sx={{ minHeight: 44, textTransform: "none" }}>
                        {t("common.close", "Close")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
